import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { EChartsOption, SeriesOption } from 'echarts';
import {
  IReitProjectionAffoToDpsYieldGraphItem,
  ISeries,
} from '../../../interfaces';
import { formatPercentage } from '@kolytics/shared-functions';
import { colorPallete, GraphId } from '@kolytics/types/graph';
import { GraphComponent } from '../../../../../../../../../libs/shared-components/src/lib/components/graph/graph.component';
import { SectionComponent } from '../../../../../../../../../libs/shared-components/src/lib/components/section/section.component';

@Component({
  selector: 'klt-projection-affo-dps-yield',
  templateUrl: './projection-affo-dps-yield.component.html',
  styleUrls: ['./projection-affo-dps-yield.component.scss'],
  standalone: true,
  imports: [SectionComponent, GraphComponent],
})
export class ProjectionAffoDpsYieldComponent {
  @Input() height = '415px';
  @Input() selectedScenarioName = 'base';
  @Input() loading: boolean | null = false;
  @Input()
  public set items(
    value: IReitProjectionAffoToDpsYieldGraphItem[] | undefined,
  ) {
    this.reset(value || []);
  }

  @Input() public options: EChartsOption | undefined = {
    grid: {
      show: true,
      borderWidth: 0,
      containLabel: true,
      top: 10,
      right: 10,
      bottom: 0,
      left: 5,
    },
    xAxis: {
      type: 'category',
      data: ['17A', '18A', '19A', '20A', '21F', '22F', '23F', '24F', '25F'],
      boundaryGap: false,
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        margin: 22,
      },
      splitArea: {
        show: true,
      },
      axisPointer: {
        show: true,
        type: 'none',
        label: {
          show: false,
        },
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value: any, index: number) => {
          return formatPercentage(value, 1);
        },
        margin: 20,
      },
      axisLine: {
        show: false,
      },
      splitLine: {
        show: true,
      },
    },
    series: [],
    tooltip: {
      trigger: 'item',
      padding: 0,
      position: 'top',
      displayMode: 'single',
      confine: true,
      formatter: function (params: any) {
        if (params.componentType && params.componentType === 'series') {
          return `<div style="background: ${
            params.color
          }; color: var(--color--text-light); padding: var(--size--spacing-x2) var(--size--spacing-x4);">${params.seriesName.toUpperCase()}-${
            params.name
          }: (${formatPercentage(params.value, 1)})</div>`;
        }
        return '';
      },
    },
  };

  public series: ISeries[] = [];

  public lines = [];

  public titles: {
    name: string;
    color: string;
    disabled: boolean;
    text?: string;
  }[] = [
    {
      name: 'AFFO',
      text: `AFFO (${this.selectedScenarioName})`,
      color: colorPallete[GraphId.ProjectionAffoDpsYield][0],
      disabled: false,
    },
    {
      name: 'DPS',
      text: `DPS (${this.selectedScenarioName})`,
      color: colorPallete[GraphId.ProjectionAffoDpsYield][1],
      disabled: false,
    },
  ];

  constructor(private cdr: ChangeDetectorRef) {}

  public runFilter(
    data: { name: string; color: string; disabled: boolean; text?: string }[],
  ): void {
    this.titles = data;

    this.titles[0].text = `AFFO (${this.selectedScenarioName})`;
    this.titles[1].text = `DPS (${this.selectedScenarioName})`;

    const filter = data
      .filter((e) => !e.disabled)
      .map((e) => e.name.toLowerCase());
    const series = this.series.filter(
      (e) => e?.name && filter.includes(e.name.toString().toLowerCase()),
    );

    (this.options as EChartsOption).series = [
      ...(series as any),
      ...this.lines,
    ];

    const temp = Object.assign({}, this.options);

    this.options = undefined;

    setTimeout(() => {
      this.options = temp;
      this.cdr.detectChanges();
    }, 0);
  }

  public reset(value: IReitProjectionAffoToDpsYieldGraphItem[] = []): void {
    if (value.length > 0) {
      value = value.sort((a, b) => a.year - b.year);

      const middle =
        value.length % 2 === 0
          ? value.length / 2 - 1
          : parseInt((value.length / 2).toString().split('.')[0], 0);
      const actuals = value.filter((e) => e.label.endsWith('A'));
      const lastActual = actuals[actuals.length - 1];

      ((this.options as EChartsOption).xAxis as any).data = value.map(
        (e) => e.label,
      );
      const dp = this.getDP(value, middle, lastActual.year);
      const affo = this.getAffo(value, middle, lastActual.year);
      this.series = [...dp, ...affo] as any;
      this.runFilter(this.titles);
    } else {
      (this.options as EChartsOption).series = [];
    }
  }

  public getDP(
    value: IReitProjectionAffoToDpsYieldGraphItem[] = [],
    middle: number,
    lastActual: number,
  ) {
    return [
      {
        data: value.map((e) => (e.year <= lastActual ? e.dpsYield : null)),
        type: 'line',
        smooth: true,
        showSymbol: false,
        color: colorPallete[GraphId.ProjectionAffoDpsYield][1],
        name: 'DPS',
        lineStyle: {
          width: 4,
        },
      },
      {
        data: value.map((e) => (e.year >= lastActual ? e.dpsYield : null)),
        type: 'line',
        smooth: true,
        showSymbol: false,
        color: colorPallete[GraphId.ProjectionAffoDpsYield][1],
        name: 'DPS',
        lineStyle: {
          type: 'dashed',
          width: 4,
        },
      },
    ];
  }

  public getAffo(
    value: IReitProjectionAffoToDpsYieldGraphItem[] = [],
    middle: number,
    lastActual: number,
  ) {
    return [
      {
        data: value.map((e) => (e.year <= lastActual ? e.affo : null)),
        type: 'line',
        smooth: true,
        showSymbol: false,
        color: colorPallete[GraphId.ProjectionAffoDpsYield][0],
        name: 'AFFO',
        lineStyle: {
          width: 4,
        },
      },
      {
        data: value.map((e) => (e.year >= lastActual ? e.affo : null)),
        type: 'line',
        smooth: true,
        showSymbol: false,
        color: colorPallete[GraphId.ProjectionAffoDpsYield][0],
        name: 'AFFO',
        lineStyle: {
          type: 'dashed',
          width: 4,
        },
      },
    ] as SeriesOption[];
  }
}
